// A stand-in for the engine's response to the controls, used while the voice is
// the mock processor, which takes only a crank speed. It turns throttle, ignition
// and starter into an rpm: the starter cranks, the engine catches after a moment
// with the ignition on, the speed follows the throttle with a lag, and it runs
// down when the ignition goes off.
//
// The voice with its own dynamics (#28 and the piston module) takes throttle,
// ignition and starter itself; then the driver sends those and this model rests.
// Every number here is an estimate for a plausible feel, not CT data.

export interface StandInInputs {
  throttle: number;
  ignition: boolean;
  starter: boolean;
}

export interface StandInState {
  rpm: number;
  running: boolean;
  /** Seconds the starter has cranked with the ignition on, towards catching. */
  cranked: number;
}

export const STAND_IN = {
  /** Estimate: a usual idle for an air-cooled VW flat-four. */
  idleRpm: 900,
  /** Estimate: a little above the CT's rated speed; the mock just needs a range to show. */
  maxRpm: 4500,
  /** Estimate: a 12 V starter turns a flat-four at about 200 to 300 rpm. */
  crankRpm: 250,
  /** Estimate: cranking time before a warm engine catches. */
  catchSeconds: 0.6,
  /** Time constants of the speed's approach to its target, in seconds. */
  riseTau: 0.35,
  fallTau: 0.8,
  starterTau: 0.15,
  stopTau: 0.6,
  /** Below this the engine has stopped. */
  stopRpm: 20,
} as const;

export const STOPPED: StandInState = { rpm: 0, running: false, cranked: 0 };

/** The state of an engine already running at the target for these inputs (for a link that opens a running engine). */
export function warmStart(inputs: StandInInputs): StandInState {
  if (!inputs.ignition) return { ...STOPPED };
  return { rpm: targetRpm(inputs.throttle), running: true, cranked: 0 };
}

export function targetRpm(throttle: number): number {
  const t = Math.min(1, Math.max(0, throttle));
  return STAND_IN.idleRpm + t * (STAND_IN.maxRpm - STAND_IN.idleRpm);
}

/** Advances the model by `dt` seconds. */
export function step(state: StandInState, inputs: StandInInputs, dt: number): StandInState {
  let { rpm, running, cranked } = state;
  if (!inputs.ignition) {
    running = false;
    cranked = 0;
  }
  let target: number;
  let tau: number;
  if (running) {
    target = targetRpm(inputs.throttle);
    tau = target >= rpm ? STAND_IN.riseTau : STAND_IN.fallTau;
  } else if (inputs.starter) {
    target = STAND_IN.crankRpm;
    tau = STAND_IN.starterTau;
    if (inputs.ignition) {
      cranked += dt;
      if (cranked >= STAND_IN.catchSeconds) {
        running = true;
        cranked = 0;
      }
    }
  } else {
    target = 0;
    tau = STAND_IN.stopTau;
    cranked = 0;
  }
  rpm = target + (rpm - target) * Math.exp(-dt / tau);
  if (!running && !inputs.starter && rpm < STAND_IN.stopRpm) rpm = 0;
  return { rpm, running, cranked };
}
